/**
 * Contratos de estoque e produção (Fase 3). O mesmo schema valida o formulário
 * no frontend e a requisição na API; o banco repete as regras essenciais.
 */
import { z } from "zod";
import { cadastroCodeSchema, decimalSchema, statusSchema, versionSchema } from "./cadastros";

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === "" ? null : v))
    .nullable()
    .optional()
    .transform((v) => v ?? null);
const isoDate = z
  .union([z.iso.date("Data inválida"), z.literal(""), z.null()])
  .optional()
  .transform((v) => (v ? v : null));
const requiredDate = z.iso.date("Informe a data");
const optionalUuid = z
  .union([z.uuid(), z.literal(""), z.null()])
  .optional()
  .transform((v) => (v ? v : null));
const positive = (scale = 6) =>
  decimalSchema(scale).refine((v) => Number(v) > 0, "A quantidade precisa ser maior que zero");

export const reasonSchema = z.string().trim().min(3, "Informe o motivo (mínimo 3 letras)").max(500);

// ---------------------------------------------------------------- status de documento
export const DOC_STATUSES = ["rascunho", "confirmado", "cancelado", "estornado"] as const;
export type DocStatus = (typeof DOC_STATUSES)[number];
export const DOC_STATUS_LABEL: Record<DocStatus, string> = {
  rascunho: "Rascunho",
  confirmado: "Confirmado",
  cancelado: "Cancelado",
  estornado: "Estornado",
};

export const reversalSchema = z.object({ reason: reasonSchema });
export const docQuerySchema = z.object({
  status: z.enum([...DOC_STATUSES, "todos"]).default("todos"),
  q: z.string().trim().max(100).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});

// ---------------------------------------------------------------- depósitos
export const WAREHOUSE_KIND_LABEL = {
  fabrica: "Fábrica",
  vendedor: "Estoque do vendedor",
  terceiro: "Terceiros / outro",
} as const;
export type WarehouseKind = keyof typeof WAREHOUSE_KIND_LABEL;

export const warehouseInputSchema = z.object({
  code: cadastroCodeSchema,
  name: z.string().trim().min(2, "Informe o nome").max(80),
  // estoque de vendedor é criado automaticamente com o vendedor
  kind: z.enum(["fabrica", "terceiro"]).default("fabrica"),
  isDefault: z.boolean().default(false),
  status: statusSchema,
});
export const warehouseUpdateSchema = z.object({
  name: z.string().trim().min(2, "Informe o nome").max(80),
  isDefault: z.boolean().default(false),
  status: statusSchema,
  ...versionSchema.shape,
});
export type WarehouseInput = z.input<typeof warehouseInputSchema>;

export type Warehouse = {
  id: string;
  code: string;
  name: string;
  kind: WarehouseKind;
  salespersonId: string | null;
  salespersonName: string | null;
  isDefault: boolean;
  status: "active" | "inactive";
  version: number;
};

// ---------------------------------------------------------------- lotes
/** Lote de um item: um lote existente OU um novo (código + datas). */
export const newLotSchema = z
  .object({
    code: z
      .string()
      .trim()
      .regex(/^[A-Za-z0-9][A-Za-z0-9._/-]{0,39}$/, "Lote: até 40 letras, números ou . _ / -"),
    manufacturedOn: isoDate,
    expiresOn: isoDate,
  })
  .refine((v) => !v.manufacturedOn || !v.expiresOn || v.expiresOn >= v.manufacturedOn, {
    message: "A validade não pode ser anterior à fabricação.",
    path: ["expiresOn"],
  });
export type NewLot = z.output<typeof newLotSchema>;

export type Lot = {
  id: string;
  productId: string;
  productCode: string;
  productName: string;
  code: string;
  manufacturedOn: string | null;
  expiresOn: string | null;
  origin: string;
  balance: string;
  daysToExpire: number | null;
};

// ---------------------------------------------------------------- movimentos e saldos
export const MOVEMENT_KIND_LABEL: Record<string, string> = {
  producao_entrada: "Produção (entrada)",
  producao_consumo: "Produção (consumo)",
  producao_perda: "Produção (perda)",
  ajuste: "Ajuste",
  inventario: "Inventário",
  compra: "Compra",
  venda: "Venda",
  carga: "Carga",
  devolucao: "Devolução",
  acerto: "Acerto",
  transferencia: "Transferência",
  regularizacao: "Regularização de venda sem estoque",
};

export const balanceQuerySchema = z.object({
  q: z.string().trim().max(100).optional(),
  warehouseId: z.uuid().optional(),
  productId: z.uuid().optional(),
  comSaldo: z
    .enum(["1", "0"])
    .default("1")
    .transform((v) => v === "1"),
  limit: z.coerce.number().int().min(1).max(500).default(100),
  offset: z.coerce.number().int().min(0).default(0),
});

export type StockBalance = {
  warehouseId: string;
  warehouseName: string;
  productId: string;
  productCode: string;
  productName: string;
  baseUnitCode: string;
  lotId: string | null;
  lotCode: string | null;
  expiresOn: string | null;
  quantity: string;
  /** custo médio do produto (na unidade-base); null = ainda sem custo */
  averageCost: string | null;
  /** quantidade × custo médio */
  totalValue: string | null;
  /**
   * v7: confiabilidade do custo — "ausente" (produto sem custo médio) ou "provisorio" (há venda
   * sem estoque a regularizar: o custo dessa saída só é apurado na regularização e a próxima
   * entrada pode redefinir o médio). null = custo médio normal.
   */
  costStatus: "ausente" | "provisorio" | null;
};

export const movementQuerySchema = z.object({
  warehouseId: z.uuid().optional(),
  productId: z.uuid().optional(),
  lotId: z.uuid().optional(),
  kind: z.string().max(40).optional(),
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});

export type StockMovement = {
  id: string;
  occurredAt: string;
  kind: string;
  direction: 1 | -1;
  productId: string;
  productCode: string;
  productName: string;
  warehouseName: string;
  lotCode: string | null;
  unitCode: string;
  quantity: string;
  quantityBase: string;
  baseUnitCode: string;
  /** custo por unidade-base registrado no movimento */
  unitCost: string | null;
  sourceType: string;
  sourceId: string;
  sourceNumber: string | null;
  isReversal: boolean;
  userEmail: string | null;
};

// ---------------------------------------------------------------- ajustes
const lotChoice = {
  lotId: optionalUuid,
  newLot: newLotSchema
    .nullable()
    .optional()
    .transform((v) => v ?? null),
};

export const adjustmentItemSchema = z
  .object({
    productId: z.uuid("Selecione o produto"),
    unitId: z.uuid("Selecione a unidade"),
    direction: z.enum(["entrada", "saida"]),
    quantity: positive(),
    ...lotChoice,
    notes: optionalText(300),
  })
  .refine((v) => !(v.lotId && v.newLot), {
    message: "Escolha um lote OU informe um novo.",
    path: ["lotId"],
  })
  .refine((v) => !v.newLot || v.direction === "entrada", {
    message: "Lote novo só em entrada.",
    path: ["newLot"],
  });

export const adjustmentInputSchema = z.object({
  warehouseId: z.uuid("Selecione o depósito"),
  reason: reasonSchema,
  items: z.array(adjustmentItemSchema).min(1, "Inclua ao menos um item").max(200),
});
export type AdjustmentInput = z.input<typeof adjustmentInputSchema>;

export type DocumentHeader = {
  id: string;
  number: string;
  status: DocStatus;
  warehouseId: string;
  warehouseName: string;
  createdAt: string;
  createdByEmail: string | null;
  confirmedAt: string | null;
  reversedAt: string | null;
  reversalReason: string | null;
  version: number;
};

export type Adjustment = DocumentHeader & {
  reason: string;
  itemCount: number;
  items?: {
    id: string;
    productId: string;
    productCode: string;
    productName: string;
    lotCode: string | null;
    unitCode: string;
    direction: 1 | -1;
    quantity: string;
    notes: string | null;
  }[];
};

// ---------------------------------------------------------------- inventário
export const inventoryCreateSchema = z.object({
  warehouseId: z.uuid("Selecione o depósito"),
  countedOn: requiredDate,
  notes: optionalText(500),
  /** Preenche a contagem com todos os produtos que têm saldo no depósito. */
  loadBalances: z.boolean().default(false),
});
export const inventoryUpdateSchema = z.object({
  countedOn: requiredDate,
  notes: optionalText(500),
  ...versionSchema.shape,
});
export const inventoryItemSchema = z
  .object({
    productId: z.uuid("Selecione o produto"),
    unitId: z.uuid("Selecione a unidade"),
    countedQuantity: decimalSchema(6, { min: 0 }),
    ...lotChoice,
  })
  .refine((v) => !(v.lotId && v.newLot), {
    message: "Escolha um lote OU informe um novo.",
    path: ["lotId"],
  });
export type InventoryItemInput = z.input<typeof inventoryItemSchema>;

export type InventoryItem = {
  id: string;
  productId: string;
  productCode: string;
  productName: string;
  lotId: string | null;
  lotCode: string | null;
  unitId: string;
  unitCode: string;
  baseUnitCode: string;
  countedQuantity: string;
  /** saldo atual do sistema (rascunho) ou o saldo no momento da confirmação */
  expectedBase: string;
  differenceBase: string | null;
  countedBase: string;
};
export type Inventory = DocumentHeader & {
  countedOn: string;
  notes: string | null;
  itemCount: number;
  items?: InventoryItem[];
};

// ---------------------------------------------------------------- ficha técnica
export const recipeItemSchema = z.object({
  productId: z.uuid("Selecione o componente"),
  unitId: z.uuid("Selecione a unidade"),
  quantity: positive(),
  notes: optionalText(300),
});
const recipeFields = {
  name: z.string().trim().min(2, "Informe o nome da ficha").max(80),
  outputUnitId: z.uuid("Selecione a unidade"),
  outputQuantity: positive(),
  expectedLossPercent: z
    .union([decimalSchema(2, { min: 0, max: 100 }), z.literal(""), z.null()])
    .optional()
    .transform((v) => (v === "" || v === undefined ? null : v)),
  notes: optionalText(1000),
  isDefault: z.boolean().default(false),
  status: statusSchema,
  items: z
    .array(recipeItemSchema)
    .min(1, "Inclua ao menos um componente")
    .max(50)
    .refine((items) => new Set(items.map((i) => i.productId)).size === items.length, {
      message: "Componente repetido na ficha.",
    }),
};
export const recipeInputSchema = z
  .object({ productId: z.uuid("Selecione o produto"), ...recipeFields })
  .refine((v) => !v.isDefault || v.status === "active", {
    message: "A ficha padrão precisa estar ativa.",
    path: ["status"],
  });
export const recipeUpdateSchema = z
  .object({ ...recipeFields, ...versionSchema.shape })
  .refine((v) => !v.isDefault || v.status === "active", {
    message: "A ficha padrão precisa estar ativa.",
    path: ["status"],
  });
export type RecipeInput = z.input<typeof recipeInputSchema>;

export type Recipe = {
  id: string;
  productId: string;
  productCode: string;
  productName: string;
  name: string;
  outputUnitId: string;
  outputUnitCode: string;
  outputQuantity: string;
  expectedLossPercent: string | null;
  notes: string | null;
  isDefault: boolean;
  status: "active" | "inactive";
  version: number;
  items?: {
    id: string;
    productId: string;
    productCode: string;
    productName: string;
    productType: string;
    unitId: string;
    unitCode: string;
    quantity: string;
    notes: string | null;
  }[];
};

// ---------------------------------------------------------------- ordem de produção
const orderFields = {
  recipeId: optionalUuid,
  unitId: z.uuid("Selecione a unidade"),
  plannedQuantity: positive(),
  outputWarehouseId: z.uuid("Selecione o depósito de saída"),
  productionDate: requiredDate,
  lotCode: z
    .union([
      z
        .string()
        .trim()
        .refine(
          (v) => v === "" || /^[A-Za-z0-9][A-Za-z0-9._/-]{0,39}$/.test(v),
          "Lote: até 40 letras, números ou . _ / -",
        ),
      z.null(),
    ])
    .optional()
    .transform((v) => (v ? v : null)),
  expiresOn: isoDate,
  notes: optionalText(1000),
};
export const productionOrderCreateSchema = z.object({
  productId: z.uuid("Selecione o produto"),
  ...orderFields,
});
export const productionOrderUpdateSchema = z.object({
  ...orderFields,
  producedQuantity: z
    .union([positive(), z.literal(""), z.null()])
    .optional()
    .transform((v) => (v === "" || v === undefined ? null : v)),
  ...versionSchema.shape,
});
export type ProductionOrderCreateInput = z.input<typeof productionOrderCreateSchema>;

export const productionItemSchema = z.object({
  kind: z.enum(["consumo", "perda"]).default("consumo"),
  productId: z.uuid("Selecione o produto"),
  warehouseId: z.uuid("Selecione o depósito"),
  unitId: z.uuid("Selecione a unidade"),
  quantity: positive(),
  /** vazio em produto com lote = a API escolhe os lotes que vencem primeiro (FEFO) */
  lotId: optionalUuid,
  notes: optionalText(300),
});
export const productionItemsSchema = z.object({
  items: z.array(productionItemSchema).max(100),
  ...versionSchema.shape,
});
export const productionConfirmSchema = z.object({
  producedQuantity: positive(),
  ...versionSchema.shape,
});

export type ProductionOrderItem = {
  id: string;
  kind: "consumo" | "perda";
  productId: string;
  productCode: string;
  productName: string;
  controlsLot: boolean;
  warehouseId: string;
  warehouseName: string;
  lotId: string | null;
  lotCode: string | null;
  unitId: string;
  unitCode: string;
  quantity: string;
  notes: string | null;
};
export type ProductionOrder = DocumentHeader & {
  productId: string;
  productCode: string;
  productName: string;
  controlsLot: boolean;
  recipeId: string | null;
  recipeName: string | null;
  unitId: string;
  unitCode: string;
  baseUnitCode: string;
  plannedQuantity: string;
  producedQuantity: string | null;
  productionDate: string;
  lotCode: string | null;
  expiresOn: string | null;
  outputLotId: string | null;
  outputLotCode: string | null;
  yieldPercent: string | null;
  /** custo do consumo e das perdas (R$); null = algum componente sem custo */
  totalCost: string | null;
  /** custo por unidade-base do produzido */
  unitCost: string | null;
  notes: string | null;
  items?: ProductionOrderItem[];
  movements?: StockMovement[];
};

/** Rastreabilidade de um lote: de onde veio e para onde foi. */
export type LotTrace = {
  lot: Lot;
  origin: {
    productionOrderId: string;
    number: string;
    productionDate: string;
    consumed: {
      productCode: string;
      productName: string;
      lotCode: string | null;
      quantityBase: string;
      unitCode: string;
    }[];
  } | null;
  usedIn: {
    productionOrderId: string;
    number: string;
    productCode: string;
    outputLotCode: string | null;
    quantityBase: string;
  }[];
  movements: StockMovement[];
};
